import stopsData from '../data/stops.json';
import { parseDepartureTime, sortHours } from '../utils/timetable';
import ZTM_API_BASE from './ztmApi';

const API_BASE = ZTM_API_BASE;
const CKAN_BASE = 'https://ckan.multimediagdansk.pl/dataset/c24aa637-3619-4dc2-a171-a23eec8f2172/resource';

// Cache
let routesCache = null;
let tripsCache = null;
let stopsInTripsCache = null;
const stopTimesCache = new Map();

// Mapa przystanków stopId -> stopDesc
let stopsMap = null;
function getStopsMap() {
  if (stopsMap) return stopsMap;
  stopsMap = new Map();
  for (const stop of stopsData) {
    stopsMap.set(stop.stopId, stop.stopDesc || `Przystanek ${stop.stopId}`);
  }
  return stopsMap;
}

/**
 * Wyciąga dane z odpowiedzi API, która opakowuje je kluczem daty YYYY-MM-DD
 */
function extractDateKeyedData(data) {
  const today = new Date().toISOString().slice(0, 10);
  if (data[today]) return data[today];
  // Fallback: pierwszy dostępny klucz daty
  const keys = Object.keys(data).filter(k => /^\d{4}-\d{2}-\d{2}$/.test(k));
  if (keys.length > 0) {
    keys.sort();
    // Wybierz najbliższą datę >= dziś, lub ostatnią dostępną
    const future = keys.find(k => k >= today);
    return data[future || keys[keys.length - 1]];
  }
  return data;
}

/**
 * Pobiera listę linii (routes)
 */
export async function fetchRoutes() {
  if (routesCache) return routesCache;
  const res = await fetch(`${CKAN_BASE}/22313c56-5acf-41c7-a5fd-dc5dc72b3851/download/routes.json`);
  const data = await res.json();
  const dateData = extractDateKeyedData(data);
  routesCache = dateData.routes || [];
  return routesCache;
}

/**
 * Pobiera listę tras/wariantów (trips)
 */
export async function fetchTrips() {
  if (tripsCache) return tripsCache;
  const res = await fetch(`${CKAN_BASE}/b15bb11c-7e06-4685-964e-3db7775f912f/download/trips.json`);
  const data = await res.json();
  const dateData = extractDateKeyedData(data);
  tripsCache = dateData.trips || [];
  return tripsCache;
}

/**
 * Pobiera czasy odjazdów dla linii na dany dzień
 */
export async function fetchStopTimes(routeId, dateStr) {
  const cacheKey = `${dateStr}_${routeId}`;
  if (stopTimesCache.has(cacheKey)) return stopTimesCache.get(cacheKey);
  const res = await fetch(`${API_BASE}/stopTimes?date=${dateStr}&routeId=${routeId}`);
  const data = await res.json();
  const stopTimes = data.stopTimes || [];
  stopTimesCache.set(cacheKey, stopTimes);
  return stopTimes;
}

/**
 * Pobiera informację o linii (routeShortName, routeLongName)
 */
export async function fetchRouteInfo(routeId) {
  const routes = await fetchRoutes();
  return routes.find(r => r.routeId === Number(routeId)) || null;
}

/**
 * Czyści headsign z numerów przystanków w nawiasach, np. "Zaspa(1234)" -> "Zaspa"
 */
function cleanHeadsign(headsign) {
  if (!headsign) return '';
  return headsign
    .replace(/\(\d+\)/g, '')    // numery przystanków w nawiasach
    .replace(/^bezp\.\s*/i, '') // prefix "bezp."
    .replace(/^\+\s*/, '')      // prefix "+"
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Pobiera dostępne kierunki dla linii
 */
export async function fetchDirections(routeId) {
  const trips = await fetchTrips();
  const routeTrips = trips.filter(t => t.routeId === Number(routeId) && t.type !== 'NON_PASSENGER');

  // Grupuj po directionId, weź headsign z MAIN trip lub pierwszego dostępnego
  const dirMap = new Map();
  for (const trip of routeTrips) {
    if (!dirMap.has(trip.directionId)) {
      dirMap.set(trip.directionId, { directionId: trip.directionId, headsign: cleanHeadsign(trip.tripHeadsign), isMain: trip.type === 'MAIN' });
    } else if (trip.type === 'MAIN') {
      dirMap.get(trip.directionId).headsign = cleanHeadsign(trip.tripHeadsign);
      dirMap.get(trip.directionId).isMain = true;
    }
  }

  return Array.from(dirMap.values()).sort((a, b) => a.directionId - b.directionId);
}

/**
 * Buduje rozkład jazdy: siatkę przystanki x godziny z minutami
 */
export async function buildTimetable(routeId, directionId, dateStr) {
  const [trips, stopTimes] = await Promise.all([
    fetchTrips(),
    fetchStopTimes(routeId, dateStr),
  ]);

  // Tripy pasujące do kierunku
  const dirTrips = trips.filter(
    t => t.routeId === Number(routeId) && t.directionId === directionId && t.type !== 'NON_PASSENGER'
  );
  const validTripIds = new Set(dirTrips.map(t => t.tripId));

  // Filtruj stopTimes do tych tripów i tylko pasażerskie przystanki
  const filtered = stopTimes.filter(
    st => validTripIds.has(st.tripId) && st.passenger !== false && st.passenger !== 'FALSE'
  );

  if (filtered.length === 0) {
    return { stops: [], hours: [], grid: {} };
  }

  // Grupuj stopTimes po kursach (busServiceName + order = jeden fizyczny przejazd)
  // Każdy kurs to unikalna kombinacja tripId + busServiceName + order
  const coursesMap = new Map();
  for (const st of filtered) {
    const courseKey = `${st.tripId}_${st.busServiceName || 'none'}_${st.order || 0}`;
    if (!coursesMap.has(courseKey)) {
      coursesMap.set(courseKey, []);
    }
    coursesMap.get(courseKey).push(st);
  }

  // Znajdź kurs z największą liczbą przystanków — to nasz kanoniczny przebieg trasy
  let canonicalCourse = null;
  let maxStops = 0;
  for (const course of coursesMap.values()) {
    if (course.length > maxStops) {
      maxStops = course.length;
      canonicalCourse = course;
    }
  }

  const stopsMapLocal = getStopsMap();
  const canonicalStops = canonicalCourse
    .sort((a, b) => a.stopSequence - b.stopSequence)
    .map(st => ({
      stopId: st.stopId,
      stopName: stopsMapLocal.get(st.stopId) || `Przystanek ${st.stopId}`,
      stopSequence: st.stopSequence,
    }));

  // Buduj grid: { stopId: { hour: [minute, ...] } }
  const grid = {};
  const hoursSet = new Set();

  for (const st of filtered) {
    const parsed = parseDepartureTime(st.departureTime);
    if (!parsed) continue;

    const { hour, minute } = parsed;
    const displayHour = hour;
    hoursSet.add(displayHour);

    if (!grid[st.stopId]) grid[st.stopId] = {};
    if (!grid[st.stopId][displayHour]) grid[st.stopId][displayHour] = [];

    // Unikaj duplikatów (różne tripy mogą mieć ten sam przystanek o tej samej godzinie)
    if (!grid[st.stopId][displayHour].includes(minute)) {
      grid[st.stopId][displayHour].push(minute);
    }
  }

  // Sortuj minuty w każdej komórce
  for (const stopId of Object.keys(grid)) {
    for (const hour of Object.keys(grid[stopId])) {
      grid[stopId][hour].sort((a, b) => a - b);
    }
  }

  const hours = sortHours(Array.from(hoursSet));

  return { stops: canonicalStops, hours, grid };
}

/**
 * Pobiera powiązania przystanków z trasami (stopsInTrips)
 */
async function fetchStopsInTrips() {
  if (stopsInTripsCache) return stopsInTripsCache;
  const res = await fetch(`${CKAN_BASE}/3115d29d-b763-4af5-93f6-763b835967d6/download/stopsintrips.json`);
  const data = await res.json();
  const dateData = extractDateKeyedData(data);
  stopsInTripsCache = dateData.stopsInTrip || [];
  return stopsInTripsCache;
}

/**
 * Pobiera listę linii obsługujących dany przystanek
 * Zwraca: [{ routeId, routeShortName, routeLongName, routeType, headsign }]
 */
export async function fetchLinesAtStop(stopId) {
  const numStopId = Number(stopId);
  const [stopsInTrips, routes, trips] = await Promise.all([
    fetchStopsInTrips(),
    fetchRoutes(),
    fetchTrips(),
  ]);

  // Znajdź routeIds powiązane z tym przystankiem
  const routeIds = new Set();
  for (const sit of stopsInTrips) {
    if (sit.stopId === numStopId && sit.passenger !== false) {
      routeIds.add(sit.routeId);
    }
  }

  // Zbuduj mapę routes
  const routesMap = new Map();
  for (const r of routes) {
    routesMap.set(r.routeId, r);
  }

  // Dla każdej linii pobierz headsign z MAIN tripa
  const tripsMap = new Map();
  for (const t of trips) {
    if (routeIds.has(t.routeId) && t.type !== 'NON_PASSENGER') {
      if (!tripsMap.has(t.routeId) || t.type === 'MAIN') {
        tripsMap.set(t.routeId, cleanHeadsign(t.tripHeadsign));
      }
    }
  }

  const result = [];
  for (const routeId of routeIds) {
    const route = routesMap.get(routeId);
    if (!route) continue;
    result.push({
      routeId: route.routeId,
      routeShortName: route.routeShortName,
      routeLongName: route.routeLongName,
      routeType: route.routeType,
      headsign: tripsMap.get(routeId) || route.routeLongName,
    });
  }

  return result;
}

/**
 * Buduje rozkład godzinowy dla jednej linii na jednym przystanku
 * Zwraca: { hours: [4,5,...], minutes: { [hour]: [min, ...] }, headsigns: [{ directionId, headsign }] }
 */
export async function buildStopLineTimetable(stopId, routeId, dateStr) {
  const numStopId = Number(stopId);
  const stopTimes = await fetchStopTimes(routeId, dateStr);

  // Filtruj do tego przystanku i pasażerskich
  const filtered = stopTimes.filter(
    st => st.stopId === numStopId && st.passenger !== false && st.passenger !== 'FALSE'
  );

  if (filtered.length === 0) {
    return { hours: [], minutes: {} };
  }

  const minutes = {};
  const hoursSet = new Set();

  for (const st of filtered) {
    const parsed = parseDepartureTime(st.departureTime);
    if (!parsed) continue;

    const { hour, minute } = parsed;
    hoursSet.add(hour);

    if (!minutes[hour]) minutes[hour] = [];
    if (!minutes[hour].includes(minute)) {
      minutes[hour].push(minute);
    }
  }

  // Sortuj minuty
  for (const hour of Object.keys(minutes)) {
    minutes[hour].sort((a, b) => a - b);
  }

  const hours = sortHours(Array.from(hoursSet));

  return { hours, minutes };
}
